import { Component, inject, NgZone, Renderer2 } from '@angular/core';
import { ValueListService } from './Common/ValueListService/value-list.service';
import { FilteredKeyValueRequestDTO } from './Common/ValueListService/valuelist.interface';
import { WhiteLabelServiceService } from './AdvERP/companies/white-label/white-label-service.service';
import { CompanyWhiteLabelDTO } from './AdvERP/companies/white-label/white-label.interface';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { UserLoginService } from './Common/UserLoginService/userlogin.service';
import { filter } from 'rxjs/operators';
import { CustomHtmlMasterService } from './Common/CustomHtmlMaster/CustomHtmlMaster.service';
import { CustomHtmlMasterItem, CustomHtmlMasterReadByMasterIdRequestDTO } from './Common/CustomHtmlMaster/CustomHtmlMaster.interface';
import { UserLoginResponseDTO } from './Common/UserLoginService/userlogin.interface';
import { ComponentBridgeService } from './Common/Services/component-bridge.service';
import { environment } from 'src/environments/environment';
import { FilterRouteSyncService } from './Shared/filter-session/filter-route-sync.service';
import { ApiConfigurationService } from './Common/ApiConfigurationService/api-configuration.service';
import { ApiConfigurationDTO } from './Common/ApiConfigurationService/api-configuration.interface';
import { AppUpdateService } from './Common/Services/app-update.service';
import { DocumentEmailPreviewService } from './Shared/document-email-preview/document-email-preview.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  title = 'AngularClient';

  valuelistService = inject(ValueListService)
  whileLabelService = inject(WhiteLabelServiceService)
  renderer = inject(Renderer2)
  private ngZone = inject(NgZone)
  sanitizer = inject(DomSanitizer)
  route = inject(ActivatedRoute)
  router = inject(Router)
  private userService = inject(UserLoginService);
  private filterRouteSyncService = inject(FilterRouteSyncService);
  currentUser: UserLoginResponseDTO | null = null;
  customHtmlService = inject(CustomHtmlMasterService)
  private componentBridge = inject(ComponentBridgeService);
  private apiConfigurationService = inject(ApiConfigurationService);
  appUpdate = inject(AppUpdateService);
  private documentEmailPreview = inject(DocumentEmailPreviewService);

  whiteLabelDetails!: CompanyWhiteLabelDTO;

  // MasterType for custom HTML that targets the always-present Home shell (header slot),
  // injected on every route in addition to that route's own MasterType.
  private static readonly GLOBAL_HEADER_MASTER_TYPE = '/global/header';

  // Cache for custom HTML by route
  private customHtmlCache = new Map<string, CustomHtmlMasterItem[]>();
  private injectionRunId = 0;
  private pendingInjectionTimeouts = new Set<number>();
  private activeInjectionObservers = new Set<MutationObserver>();

  // Route to MasterType mapping.
  // Entries with a trailing '/' are prefix patterns (checked via startsWith in
  // getMasterTypeForRoute) - they intentionally do NOT match the bare list
  // route itself, only routes with something after the trailing slash (e.g.
  // an :id segment). This is required whenever a list route and its detail
  // route (list/:id) would otherwise collapse to the same masterType via
  // extractBaseRoute()'s first-two-segments fallback, which serves the
  // list page's cached/fetched custom HTML on the detail page (and vice
  // versa on re-navigation) - see docs/custom-pages.md "route-prefix
  // collapsing" trap. Add an entry here for any other
  // list+detail pair that shows the wrong/stale custom HTML after
  // navigating between the two.
  private routeMasterTypeMap: { [key: string]: string } = {

  };
  ngOnInit() {

    // Handle ChunkLoadError on route navigation
    this.setupChunkErrorHandler();
    this.appUpdate.start();

    // Expose environment URLs to window.ERP
    this.exposeEnvironmentUrls();
    this.filterRouteSyncService.init();

    this.whileLabelService.$whiteLabelDetails.subscribe(res => {
      this.whiteLabelDetails = res as CompanyWhiteLabelDTO
      this.updateCompanyStyle()
    })

    // 🔥 AUTO-INJECT CUSTOM HTML ON EVERY ROUTE CHANGE
    this.setupAutoInjection();
    this.exposeReInjectionToWindow();

    // USER OBJECT
    this.userService.user$.subscribe(u => {
      this.currentUser = u;
      if (u) {
        // Re-expose window.ERP right before injecting: the ngOnInit call
        // above can run before login.component's loadConfiguration(tenantId)
        // resolves (this component is mounted for the whole app, login page
        // included). By the time a user is actually present here, the
        // ServerPool override is guaranteed loaded - refresh window.ERP with
        // it now rather than leaving it stuck on whatever was resolved (or
        // defaulted) at the earlier, possibly-too-early call.
        this.exposeEnvironmentUrls();
        const currentRoute = this.router.url.split('?')[0];
        this.handleCustomHtmlInjection(currentRoute);
      }
    });

    // optionally set currentUser immediately from storage fallback
    this.currentUser = this.userService.getCurrentUser();

  }


  /**
   * Setup handler for ChunkLoadError during route navigation
   * This prevents the app from breaking when lazy-loaded chunks fail to load
   * (typically after a new deployment while users have the old app open)
   */
  private setupChunkErrorHandler(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Reset reload flag on successful navigation
      sessionStorage.removeItem('chunk_error_reload');
    });

    // Listen for navigation errors
    this.router.events.subscribe(event => {
      if ((event as any).error) {
        const error = (event as any).error;
        const isChunkError = error?.name === 'ChunkLoadError' ||
          error?.message?.includes('Loading chunk');

        if (isChunkError && !sessionStorage.getItem('chunk_error_reload')) {
          console.warn('⚠️ ChunkLoadError during navigation. Reloading...');
          sessionStorage.setItem('chunk_error_reload', 'true');
          window.location.reload();
        }
      }
    });
  }


  // Exposes each gateway base URL onto window.ERP for custom HTML pages
  // (they call axios directly, never through Angular's HttpClient, so
  // ApiUrlRewriteInterceptor never sees - and never rewrites - those calls).
  // Previously this always used the compiled-in environment.*BaseURL
  // default, regardless of tenant. For any tenant on a dedicated/pool
  // server (e.g. Sharda Cards - apig.scpl.*.advinsoftwares.com), that's the
  // WRONG host: custom HTML kept calling the shared default gateway while
  // Angular's own service calls correctly used the ServerPool-resolved
  // override via ApiUrlRewriteInterceptor. Now prefers
  // ApiConfigurationService.getOverrides() (same source the interceptor
  // uses) and only falls back to the environment default for a module the
  // tenant's ServerPool row leaves unoverridden.
  private exposeEnvironmentUrls(): void {
    const overrides = environment.production
      ? this.apiConfigurationService.getOverrides()
      : null;

    const resolve = (moduleKey: keyof ApiConfigurationDTO, fallback: string): string => {
      // GetApiConfiguration's actual JSON keys are lowercase (e.g. "erp", "am"),
      // and ApiConfigurationDTO now declares them lowercase too (2026-09-22 fix -
      // it previously declared uppercase keys, which required this function to
      // lowercase moduleKey before indexing; that workaround is gone now that the
      // interface itself matches the real response shape).
      const raw = overrides?.[moduleKey];
      // ServerPool's GetApiConfiguration always returns a trailing slash
      // (e.g. "https://apig.scpl.erp.advinsoftwares.com/"); strip it so it
      // matches the no-trailing-slash shape environment.*BaseURL has and
      // custom HTML already assumes (`${ERPbaseURL}/ERP/...`).
      return raw ? raw.replace(/\/+$/, '') : fallback;
    };

    this.componentBridge.exposeData({
      AMBaseURL: resolve('am', environment.AMBaseURL),
      AIBaseURL: resolve('ai', environment.AIBaseURL),
      BIRBaseURL: resolve('bir', environment.BIRBaseURL),
      CRMBaseURL: resolve('crm', environment.CRMBaseURL),
      DOBaseURL: resolve('do', environment.DOBaseURL),
      ERPBaseURL: resolve('erp', environment.ERPBaseURL),
      EXTBaseURL: resolve('ext', environment.EXTBaseURL),
      HRMSBaseURL: resolve('hrms', environment.HRMSBaseURL),
      NTBaseURL: resolve('nt', environment.NTBaseURL),
      PMSBaseURL: resolve('pms', environment.PMSBaseURL),
      PRBaseURL: resolve('pr', environment.PRBaseURL),
      SPDBaseURL: resolve('spd', environment.SPDBaseURL)
    });
  }

  private exposeReInjectionToWindow() {
    const w = window as any;
    w.ERP = w.ERP || {};

    this.documentEmailPreview.registerWindowBridge();

    w.ERP.reInjectCustomHtml = () => {
      const currentRoute = this.router.url.split('?')[0];
      this.handleCustomHtmlInjection(currentRoute);
    };
  }
  // ngOnInit() {
  //   let CompanyId = 0
  //   const fullUrl = window.location.href.split('/');

  //   if (fullUrl.includes('login')) {
  //     this.route.queryParamMap.subscribe(params => {
  //       const companyId = params.get('CompanyId');

  //       if (companyId) {
  //         CompanyId = parseInt(companyId)
  //       } else {
  //         const host = window.location.host;
  //         let data: FilteredKeyValueRequestDTO = {
  //           dCode: 'HostCompanyId',
  //           loggedInCompanyId: 1,
  //           FilterValue: 'boxtrade.advinsoftwares.com'
  //         }
  //         this.valuelistService.ValueListItem_GetFilteredKeyValueDictionaryByCode(data).subscribe(res => {
  //           CompanyId = +res.items[0].key
  //         })
  //       }
  //     });
  //   }




  //   this.getWhiteLabelForCompany(CompanyId)
  // }



  // getWhiteLabelForCompany(companyId: number) {
  //   const tenantId = companyId 
  //   this.whileLabelService.CompanyWhiteLabel_readByTenantId({ tenantId }).subscribe(res => {
  //     this.whiteLabelDetails = res
  //     this.updateCompanyStyle()
  //   })
  // }


  updateCompanyStyle() {
    if (this.whiteLabelDetails) {
      const { favicon, primaryColor, secondaryColor, companyLogo, smallLogoUrl, loginPageHtml, styleCss } = this.whiteLabelDetails;
      const style = this.renderer.createElement('style');
      const css = styleCss;
      this.renderer.appendChild(style, this.renderer.createText(css as string));
      this.renderer.appendChild(document.head, style);

      const safeHtml = this.sanitizer.bypassSecurityTrustHtml(loginPageHtml!);
      // this.companyCoverRef.nativeElement.innerHTML = safeHtml as string

      // Favicon logic
      if (favicon) {
        let faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

        if (!faviconLink) {
          faviconLink = document.createElement('link');
          faviconLink.rel = 'icon';
          document.head.appendChild(faviconLink);
        }

        // To prevent caching issues (optional)
        faviconLink.href = `${favicon}?v=${new Date().getTime()}`;
      }

      // this.companyLargeLogoRef.nativeElement.src = companyLogo as string
      // this.companySmallLogoRef.nativeElement.src = smallLogoUrl as string

    }
  }

  /**
   * 🚀 AUTO-INJECTION: Monitors route changes and injects custom HTML
   */
  private setupAutoInjection() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEvent = event as NavigationEnd;
        const currentRoute = navEvent.urlAfterRedirects.split('?')[0]; // Remove query params
        this.handleCustomHtmlInjection(currentRoute);
      });
  }

  /**
   * Handles custom HTML injection for a given route
   */
  private async handleCustomHtmlInjection(route: string) {
    const runId = ++this.injectionRunId;
    this.cancelPendingInjections();
    console.log(`🔧 [DEBUG] handleCustomHtmlInjection called for route: ${route}`);
    let masterType = this.getMasterTypeForRoute(route);

    if (!masterType) {
      const baseRoute = this.extractBaseRoute(route);
      masterType = baseRoute;
    }

    const hasRouteMasterType = !!masterType && masterType != '/';
    if (!hasRouteMasterType) {
      console.log(`❌ [DEBUG] No custom HTML configured for route: ${route}`);
    } else {
      console.log(`✅ [DEBUG] MasterType found: ${masterType} for route: ${route}`);
    }

    try {
      // Header-level custom HTML (e.g. the calculator) lives in the Home shell, which is on
      // every route, so it is registered once under GLOBAL_HEADER_MASTER_TYPE and merged in
      // here instead of needing one CustomHtmlMaster row per page route.
      const globalItems = await this.loadCustomHtmlForRoute(AppComponent.GLOBAL_HEADER_MASTER_TYPE).catch(() => [] as CustomHtmlMasterItem[]);
      const routeItems = hasRouteMasterType ? await this.loadCustomHtmlForRoute(masterType as string) : [];
      const customHtmlItems = [...globalItems, ...routeItems];

      console.log(`📊 [DEBUG] Loaded ${customHtmlItems.length} item(s) for ${masterType}`);

      if (customHtmlItems.length > 0) {
        console.log(`✅ Injecting custom HTML for: ${masterType}`);
        this.injectCustomHtml(customHtmlItems, 0, runId);
      } else {
        console.warn(`⚠️ [DEBUG] No items returned from API for ${masterType}`);
      }
    } catch (error) {
      console.error(`❌ Failed to inject custom HTML for ${masterType}:`, error);
    }
  }



  /**
    * Extracts the base route (first two segments) from a full route
   * @param fullRoute 
   * @returns 
   */
  private extractBaseRoute(fullRoute: string): string {
    // Remove query params
    const pathWithoutQuery = fullRoute.split('?')[0];

    // Split by '/' and take first two segments (e.g., '/sale/sales' from '/sale/sales/317')
    const segments = pathWithoutQuery.split('/').filter(s => s); // Remove empty strings

    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }

    return pathWithoutQuery;
  }

  /**
   * Gets the MasterType based on current route
   */
  private getMasterTypeForRoute(route: string): string | null {
    console.log(`🔍 [DEBUG] Looking for masterType for route: "${route}"`);
    console.log(`🔍 [DEBUG] Available routes in map:`, Object.keys(this.routeMasterTypeMap));

    // Exact match
    if (this.routeMasterTypeMap[route]) {
      console.log(`✅ [DEBUG] Exact match found: ${this.routeMasterTypeMap[route]}`);
      return this.routeMasterTypeMap[route];
    }

    // Partial match (e.g., /purchase/purchase-order/123 matches /purchase/purchase-order)
    // Sort by pattern length descending so more-specific patterns (e.g. /sale/sales-challan)
    // are checked before shorter overlapping ones (e.g. /sale/sales).
    const sortedEntries = Object.entries(this.routeMasterTypeMap)
      .sort((a, b) => b[0].length - a[0].length);
    for (const [routePattern, masterType] of sortedEntries) {
      if (route.startsWith(routePattern)) {
        console.log(`✅ [DEBUG] Partial match found: ${routePattern} -> ${masterType}`);
        return masterType;
      }
    }

    console.log(`❌ [DEBUG] No match found for route: ${route}`);
    return null;
  }

  /**
   * Loads custom HTML from API (with caching)
   */
  private async loadCustomHtmlForRoute(masterType: string): Promise<CustomHtmlMasterItem[]> {
    // Return cached data if available
    if (this.customHtmlCache.has(masterType)) {
      console.log(`📦 Using cached HTML for: ${masterType}`);
      return this.customHtmlCache.get(masterType)!;
    }

    // inside loadCustomHtmlForRoute (before creating `data`):
    const user = this.currentUser || this.userService.getCurrentUser();
    if (!user) {
      console.warn('⚠️ [DEBUG] User not available; skipping custom HTML load for', masterType);
      return [];
    }

    console.log(`📡 [DEBUG] Loading custom HTML for masterType: ${masterType}`);
    console.log(`📡 [DEBUG] User context: CompanyId=${user.companyId}, BranchId=${user.loggedInBranchId}`);

    const data: CustomHtmlMasterReadByMasterIdRequestDTO = {
      tenantId: user.companyId,
      tenantBranchId: user.loggedInBranchId,
      masterType
    };

    return new Promise((resolve, reject) => {
      this.customHtmlService.CustomHtmlMaster_ReadByMasterId(data).subscribe({
        next: (res) => {
          console.log(`📦 [DEBUG] API Response for ${masterType}:`, res);
          const items = res?.items || [];
          console.log(`📦 [DEBUG] Items count: ${items.length}`);
          items.forEach((item, index) => {
            console.log(`📦 [DEBUG] Item ${index + 1}:`, {
              parentDivId: item.parentDivId,
              chtmlLength: item.chtml?.length || 0,
              chtmlPreview: item.chtml?.substring(0, 200) || 'No content'
            });
            // 🔥 Show full item structure to identify property names
            console.log(`📦 [DEBUG] Full item ${index + 1} object:`, item);
            console.log(`📦 [DEBUG] Item ${index + 1} keys:`, Object.keys(item));
          });
          this.customHtmlCache.set(masterType, items);
          console.log(`✅ Loaded and cached HTML for: ${masterType}`);
          resolve(items);
        },
        error: (err) => {
          console.error(`❌ Error loading custom HTML for ${masterType}:`, err);
          reject(err);
        }
      });
    });
  }

  /**
   * Injects custom HTML into DOM (with retry logic for dynamic content)
   */
  private injectCustomHtml(customHtmlItems: CustomHtmlMasterItem[], retryCount = 0, runId = this.injectionRunId) {
    if (runId !== this.injectionRunId) {
      return;
    }

    const maxRetries = 10;
    const retryDelay = 500; // ms
    const useObserver = retryCount >= 3; // Switch to observer after 3 retries

    if (useObserver) {
      // Use MutationObserver for elements that take long to load
      this.injectWithObserver(customHtmlItems, retryCount, runId);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      this.pendingInjectionTimeouts.delete(timeoutId);

      if (runId !== this.injectionRunId) {
        return;
      }

      console.log(`🔧 [DEBUG] injectCustomHtml retry ${retryCount}, processing ${customHtmlItems.length} item(s)`);
      let injectedCount = 0;
      let handledCount = 0;
      const pendingItems: CustomHtmlMasterItem[] = [];

      customHtmlItems.forEach((item, index) => {
        console.log(`🔧 [DEBUG] Processing item ${index + 1}/${customHtmlItems.length}`);
        console.log(`🔧 [DEBUG] Item structure:`, item);
        console.log(`🔧 [DEBUG] Item keys:`, Object.keys(item));
        let injectedThisItem = false;
        if (!item.parentDivId || !item.chtml) {
          console.warn(`⚠️ [DEBUG] Skipping item ${index + 1}: Missing parentDivId or chtml`, {
            hasParentDivId: !!item.parentDivId,
            hasChtml: !!item.chtml,
            actualParentDivId: item.parentDivId,
            actualParentDivIdType: typeof item.parentDivId,
            actualChtml: item.chtml,
            actualChtmlType: typeof item.chtml,
            actualChtmlLength: item.chtml?.length,
            allKeys: Object.keys(item)
          });
          return;
        }

        // Trim the parentDivId
        const selector = item.parentDivId.trim();
        let parentDivs: HTMLCollectionOf<Element> | HTMLElement[] = [] as any;

        // Check if selector starts with '.' for class, otherwise treat as ID
        if (selector.startsWith('.')) {
          const className = selector.substring(1); // Remove the '.'
          parentDivs = document.getElementsByClassName(className);
          console.log(`🔍 [DEBUG] Looking for class: "${className}" - Found ${parentDivs.length} element(s)`);
        } else {
          const element = document.getElementById(selector);
          if (element) {
            parentDivs = [element];
          }
          console.log(`🔍 [DEBUG] Looking for ID: "${selector}" - Found ${element ? 1 : 0} element(s)`);
        }

        if (parentDivs.length === 0) {
          console.warn(`⚠️ No elements found with selector "${selector}" - will retry this item`);
          pendingItems.push(item);
          return;
        }

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = item.chtml;

        const scripts = Array.from(tempDiv.querySelectorAll('script'));
        console.log(`📜 [DEBUG] Found ${scripts.length} script tag(s) in content`);
        scripts.forEach((s, i) => {
          console.log(`📜 [DEBUG] Script ${i + 1}:`, {
            hasSrc: !!s.getAttribute('src'),
            src: s.getAttribute('src'),
            hasInlineCode: !!s.textContent,
            inlineCodePreview: s.textContent?.substring(0, 100)
          });
          s.remove();
        });

        const htmlContent = tempDiv.innerHTML;
        console.log(`📄 [DEBUG] HTML content length (without scripts): ${htmlContent.length}`);
        const signature = this.getCustomHtmlSignature(item);

        // Inject HTML into all matching elements
        Array.from(parentDivs).forEach((parentDiv, divIndex) => {
          const alreadyInjected =
            parentDiv.getAttribute('data-custom-html-id') === String(item.customId) &&
            parentDiv.getAttribute('data-custom-html-signature') === signature &&
            parentDiv.childElementCount > 0;

          if (alreadyInjected) {
            console.log(`⏭️ [DEBUG] Skipping reinjection for customId ${item.customId}; content already up to date`);
            handledCount++;
            return;
          }

          console.log(`💉 [DEBUG] Injecting into element ${divIndex + 1}/${parentDivs.length}`);
          if (htmlContent.trim()) {
            parentDiv.innerHTML = ''; // Clear existing content only when there is HTML to replace with
            parentDiv.insertAdjacentHTML('beforeend', htmlContent);
          }
          parentDiv.setAttribute('data-custom-html-id', String(item.customId));
          parentDiv.setAttribute('data-custom-html-signature', signature);
          injectedCount++;
          handledCount++;
          injectedThisItem = true;
        });

        // Execute scripts once
        if (injectedThisItem) {
          console.log(`🚀 [DEBUG] Executing ${scripts.length} script(s)`);
          this.executeScripts(scripts);
        }
      });

      if (handledCount > 0) {
        console.log(`✅ Successfully injected ${injectedCount} custom HTML element(s)`);
      }

      if (pendingItems.length > 0 && retryCount < maxRetries) {
        console.log(`🔄 Retry ${retryCount + 1}/${maxRetries} for ${pendingItems.length} item(s) - waiting for DOM elements...`);
        this.injectCustomHtml(pendingItems, retryCount + 1, runId);
      } else if (pendingItems.length > 0) {
        console.warn(`⚠️ Failed to inject ${pendingItems.length} custom HTML item(s) after ${maxRetries} retries`);
      }
    }, retryDelay);
    this.pendingInjectionTimeouts.add(timeoutId);
  }

  /**
   * Uses MutationObserver to watch for elements and inject when they appear
   */
  private injectWithObserver(customHtmlItems: CustomHtmlMasterItem[], retryCount: number, runId = this.injectionRunId) {
    if (runId !== this.injectionRunId) {
      return;
    }

    const timeout = 10000; // 10 seconds max wait
    const startTime = Date.now();
    let hasInjected = false;
    let lastCheckTime = 0;
    const checkInterval = 500; // Check every 500ms to reduce overhead
    let checkCount = 0;
    let remainingItems = customHtmlItems.slice();

    const tryInject = () => {
      if (!remainingItems.length || runId !== this.injectionRunId) return 0;

      let injectedCount = 0;
      let handledCount = 0;
      const stillPending: CustomHtmlMasterItem[] = [];

      remainingItems.forEach((item) => {
        if (!item.parentDivId || !item.chtml) return;

        // Trim the parentDivId
        const selector = item.parentDivId.trim();
        let parentDivs: HTMLCollectionOf<Element> | NodeListOf<Element> | HTMLElement[] = [] as any;

        // Check if selector starts with '.' for class, otherwise treat as ID
        if (selector.startsWith('.')) {
          const className = selector.substring(1); // Remove the '.'
          parentDivs = document.getElementsByClassName(className);
          // Only log every 10th check to reduce console spam
          if (checkCount % 10 === 0) {
            console.log(`🔍 Searching for class "${className}" - Found ${parentDivs.length} elements (check ${checkCount})`);
          }
        } else {
          const element = document.getElementById(selector);
          if (element) {
            parentDivs = [element];
          }
          if (checkCount % 10 === 0) {
            console.log(`🔍 Searching for ID "${selector}" - Found ${element ? 1 : 0} elements (check ${checkCount})`);
          }
        }

        if (parentDivs.length === 0) {
          stillPending.push(item);
          return;
        }

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = item.chtml;

        const scripts = Array.from(tempDiv.querySelectorAll('script'));
        scripts.forEach(s => s.remove());

        const htmlContent = tempDiv.innerHTML;
        const signature = this.getCustomHtmlSignature(item);
        let injectedThisItem = false;

        Array.from(parentDivs).forEach((parentDiv) => {
          const alreadyInjected =
            parentDiv.getAttribute('data-custom-html-id') === String(item.customId) &&
            parentDiv.getAttribute('data-custom-html-signature') === signature &&
            parentDiv.childElementCount > 0;

          if (alreadyInjected) {
            handledCount++;
            return;
          }

          parentDiv.innerHTML = '';
          parentDiv.insertAdjacentHTML('beforeend', htmlContent);
          parentDiv.setAttribute('data-custom-html-id', String(item.customId));
          parentDiv.setAttribute('data-custom-html-signature', signature);
          injectedCount++;
          handledCount++;
          injectedThisItem = true;
        });

        if (injectedThisItem) {
          this.executeScripts(scripts);
          hasInjected = true;
        }
      });

      checkCount++;
      remainingItems = stillPending;
      return remainingItems.length === 0 ? Math.max(handledCount, 1) : 0;
    };

    // Try immediately first
    const immediateCount = tryInject();
    if (immediateCount > 0) {
      console.log(`✅ Injected ${immediateCount} custom HTML element(s) immediately`);
      return;
    }

    console.log(`👁️ Watching DOM for class "${customHtmlItems[0]?.parentDivId}" to appear...`);

    // MutationObserver on document.body/subtree fires on EVERY DOM mutation in the whole
    // app, not just near the target. zone.js patches MutationObserver by default, so every
    // one of those firings - including the throttled no-ops below - would re-enter Angular's
    // zone and trigger a full app-wide change-detection tick for up to `timeout` ms after
    // every navigation. On a page with normal DOM churn (grids, ng-select, etc.) that reads
    // as the UI "hanging" until the observer gives up. Running it outside the zone stops
    // unrelated DOM mutations from driving change detection; we hop back into the zone only
    // when there's an actual outcome (injected, or timed out) to report/act on.
    this.ngZone.runOutsideAngular(() => {
      const observer = new MutationObserver(() => {
        if (runId !== this.injectionRunId) {
          this.cleanupInjectionObserver(observer);
          return;
        }

        // Throttle checks to avoid performance issues
        const now = Date.now();
        if (now - lastCheckTime < checkInterval) {
          return;
        }
        lastCheckTime = now;

        const count = tryInject();

        // Stop observing if injected or timeout
        if (count > 0) {
          this.cleanupInjectionObserver(observer);
          this.ngZone.run(() => {
            console.log(`✅ Injected ${count} custom HTML element(s) after ${checkCount} checks`);
          });
        } else if (Date.now() - startTime > timeout) {
          this.cleanupInjectionObserver(observer);
          this.ngZone.run(() => {
            console.warn(`⚠️ Observer timed out after ${timeout}ms - "${customHtmlItems[0]?.parentDivId}" never appeared`);
          });
        }
      });
      this.activeInjectionObservers.add(observer);

      // Observe only necessary parts of the DOM for better performance
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Auto-disconnect after timeout
      window.setTimeout(() => {
        const wasInjected = hasInjected;
        this.cleanupInjectionObserver(observer);
        if (!wasInjected) {
          this.ngZone.run(() => {
            console.warn(`⚠️ Observer gave up after ${timeout}ms`);
          });
        }
      }, timeout);
    });
  }

  private cancelPendingInjections(): void {
    this.pendingInjectionTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    this.pendingInjectionTimeouts.clear();
    this.activeInjectionObservers.forEach(observer => observer.disconnect());
    this.activeInjectionObservers.clear();
  }

  private cleanupInjectionObserver(observer: MutationObserver): void {
    observer.disconnect();
    this.activeInjectionObservers.delete(observer);
  }

  private getCustomHtmlSignature(item: CustomHtmlMasterItem): string {
    return `${item.customId ?? 'na'}:${item.modifiedOn ?? 'na'}:${item.chtml?.length ?? 0}`;
  }

  /**
   * Executes script tags
   */
  private executeScripts(scripts: Element[]): void {
    console.log(`🚀 [DEBUG] executeScripts called with ${scripts.length} script(s)`);
    scripts.forEach((oldScript, index) => {
      try {
        console.log(`🚀 [DEBUG] Executing script ${index + 1}/${scripts.length}`);
        const newScript = document.createElement('script');

        if (oldScript.attributes) {
          console.log(`🚀 [DEBUG] Script has ${oldScript.attributes.length} attribute(s)`);
          Array.from(oldScript.attributes).forEach(attr => {
            if (attr?.name && attr.value != null) {
              console.log(`🚀 [DEBUG] Setting attribute: ${attr.name}="${attr.value}"`);
              newScript.setAttribute(attr.name, attr.value);
            }
          });
        }

        if (oldScript.textContent) {
          console.log(`🚀 [DEBUG] Script has inline content (${oldScript.textContent.length} chars):`);
          console.log(`🚀 [DEBUG] Script content preview:`, oldScript.textContent.substring(0, 300));
          newScript.textContent = oldScript.textContent;
        }

        console.log(`🚀 [DEBUG] Appending script to body`);
        document.body.appendChild(newScript);
        newScript.remove();
        console.log(`✅ [DEBUG] Script ${index + 1} executed successfully`);
      } catch (error) {
        console.error(`❌ Error executing script ${index + 1}:`, error);
        console.error(`❌ [DEBUG] Script content that failed:`, oldScript.textContent);
      }
    });
    console.log(`✅ [DEBUG] Finished executing all scripts`);
  }
}
